import { api } from "../core/api.js";
import { h, onActivate } from "../core/dom.js";
import { navigate } from "../core/router.js";
import { t } from "../core/i18n.js";
import { loadInto } from "../core/states.js";
import type { Page, ShortItem } from "../models.js";

const DISABLE_KEY = "shortdrama.spoilerWarningsOff";
const spoilerWarningsOff = (): boolean => localStorage.getItem(DISABLE_KEY) === "1";

/** Shorts vertical feed + spoiler guard (00 §12.2, 04-web §12). */
export async function shortsView(_params: Record<string, string>, root: HTMLElement): Promise<void> {
  await loadInto<Page<ShortItem>>({
    root,
    load: () => api.get<Page<ShortItem>>("/v1/shorts/feed"),
    isEmpty: (page) => page.items.length === 0,
    empty: t("shorts.empty"),
    render: (page) => {
      const cards = page.items.map(shortCard);
      const list = h(
        "div",
        { class: "shorts", role: "list", "aria-label": t("shorts.pagerLabel") },
        ...cards,
      );
      // Keyboard pager: Arrow/Home/End move focus between shorts.
      list.addEventListener("keydown", (e) => {
        const key = (e as KeyboardEvent).key;
        const focusables = cards;
        const current = focusables.indexOf(document.activeElement as HTMLElement);
        let next = -1;
        if (key === "ArrowDown" || key === "ArrowRight") next = Math.min(focusables.length - 1, current + 1);
        else if (key === "ArrowUp" || key === "ArrowLeft") next = Math.max(0, current - 1);
        else if (key === "Home") next = 0;
        else if (key === "End") next = focusables.length - 1;
        if (next >= 0) {
          e.preventDefault();
          const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          focusables[next]?.focus();
          focusables[next]?.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
        }
      });
      return list;
    },
  });
}

function shortCard(s: ShortItem): HTMLElement {
  const blurred = s.is_spoiler === true && !spoilerWarningsOff();
  const label = s.text_overlay ? `${t("shorts.openShort")}: ${s.text_overlay}` : t("shorts.openShort");
  const card = h(
    "div",
    {
      class: `short${blurred ? " spoiler" : ""}`,
      role: "listitem",
      tabindex: "0",
      "aria-label": blurred ? `${t("compliance.spoilerBadge")} — ${label}` : label,
    },
    h("div", { class: "thumb", role: "img", "aria-hidden": "true" }),
    s.text_overlay ? h("div", { class: "overlay-text" }, s.text_overlay) : null,
    blurred
      ? h("div", { class: "label-overlay" }, h("span", { class: "badge deepfake" }, t("compliance.spoilerBadge")))
      : null,
  );
  onActivate(card, () => openShort(s, card));
  return card;
}

function openShort(s: ShortItem, card: HTMLElement): void {
  if (s.is_spoiler === true && !spoilerWarningsOff()) {
    showSpoilerDialog(s, card);
  } else {
    navigate(`/shorts/${s.id}`);
  }
}

/**
 * Instagram-reels style spoiler gate. Exact copy per 00 §12.2.
 * Accessible modal: role=dialog, aria-modal, focus trap, Esc to close,
 * returns focus to the triggering card.
 */
function showSpoilerDialog(s: ShortItem, card: HTMLElement): void {
  const titleId = "spoiler-dialog-title";
  const descId = "spoiler-dialog-desc";

  const close = (): void => {
    backdrop.remove();
    document.removeEventListener("keydown", onKeydown);
    card.focus(); // return focus to the trigger
  };

  const confirmBtn = h(
    "button",
    {
      type: "button",
      class: "btn",
      onclick: () => {
        close();
        card.classList.remove("spoiler");
        navigate(`/shorts/${s.id}`);
      },
    },
    t("common.watch"),
  ) as HTMLButtonElement;

  const cancelBtn = h("button", { type: "button", class: "btn secondary", onclick: close }, t("common.cancel")) as HTMLButtonElement;

  const dialog = h(
    "div",
    { class: "dialog", role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-describedby": descId },
    h("h2", { id: titleId, class: "dialog-title" }, t("compliance.spoilerTitle")),
    h("p", { id: descId }, t("compliance.spoilerDialog")),
    h(
      "label",
      { class: "muted", style: "display:block;margin-top:10px;font-size:12px" },
      h("input", {
        type: "checkbox",
        onchange: (e: Event) => {
          if ((e.target as HTMLInputElement).checked) localStorage.setItem(DISABLE_KEY, "1");
          else localStorage.removeItem(DISABLE_KEY);
        },
      }),
      " " + t("compliance.spoilerAlwaysOff"),
    ),
    h("div", { class: "row" }, cancelBtn, confirmBtn),
  );

  const backdrop = h(
    "div",
    { class: "dialog-backdrop", onclick: (e: Event) => e.target === backdrop && close() },
    dialog,
  );

  const focusables = (): HTMLElement[] =>
    Array.from(dialog.querySelectorAll<HTMLElement>("button, input, [tabindex]"));

  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "Tab") {
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  };

  document.addEventListener("keydown", onKeydown);
  document.body.appendChild(backdrop);
  confirmBtn.focus(); // initial focus inside the dialog
}
